import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Modal, Spin, Table, Typography } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { useStore } from 'effector-react';

import IUser, { IUserDevice } from '@interfaces/IUser';
import { getPath } from '@helpers/navigation';
import routes from '@config/routes';
import useTranslate from '@hooks/useTranslate';
import userDevices, {
  getList as getUserDevices,
  reset as resetUserDevices,
} from '@stores/user-devices';
import componentContent from './UserUdidsModal.content.json';

interface UserUdidsModalProps {
  accountId?: IUser['id'];
  devicesCount?: number;
  visible: boolean;
  onClose: () => void;
}

const UserUdidsModal: FunctionComponent<UserUdidsModalProps> = ({
  accountId,
  devicesCount = 0,
  visible,
  onClose,
}) => {
  const content = useTranslate(componentContent);
  const { list, preloaders } = useStore(userDevices);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });

  const openAccountsByUdid = useCallback((udid: string) => {
    window.open(
      getPath(routes.userList.name, undefined, { filterUdid: udid }),
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const loadDevices = useCallback(
    (nextPagination: TablePaginationConfig) => {
      if (!accountId) return;

      setPagination(nextPagination);
      getUserDevices({ accountId, pagination: nextPagination });
    },
    [accountId],
  );

  useEffect(() => {
    if (!visible || !accountId) return;

    const initialPagination = { current: 1, pageSize: 10 };
    resetUserDevices();
    loadDevices(initialPagination);
  }, [accountId, loadDevices, visible]);

  const columns: ColumnsType<IUserDevice> = useMemo(
    () => [
      {
        dataIndex: 'udid',
        key: 'udid',
        title: content.udid,
        render: (udid: string) => (
          <Typography.Link onClick={() => openAccountsByUdid(udid)}>{udid}</Typography.Link>
        ),
      },
      {
        dataIndex: 'accountsCount',
        key: 'accountsCount',
        title: content.accountsCount,
      },
    ],
    [content, openAccountsByUdid],
  );

  return (
    <Modal
      visible={visible}
      title={`${content.title}: ${devicesCount}`}
      footer={null}
      onCancel={onClose}
      width={900}
      destroyOnClose
      keyboard
    >
      {preloaders.getUserDevices && list.length === 0 ? (
        <div
          style={{
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size='large' />
        </div>
      ) : list.length > 0 ? (
        <Table
          rowKey='id'
          dataSource={list}
          columns={columns}
          loading={!!preloaders.getUserDevices}
          onChange={(nextPagination) => loadDevices(nextPagination)}
          pagination={{
            ...pagination,
            total: devicesCount,
            pageSizeOptions: ['10', '20', '30'],
            showSizeChanger: true,
            position: ['topRight'],
          }}
        />
      ) : (
        <Empty imageStyle={{ height: 50 }} />
      )}
    </Modal>
  );
};

export default UserUdidsModal;
